/**
 * TASK-0032 / ADR-010: real object-storage document archive — replaces the metadata-only stub.
 *
 * Deliberately self-contained rather than built on top of `./client.ts`'s `api()` helper: `api()`
 * always sets `Content-Type: application/json` and JSON-encodes the body, which is wrong for the
 * real `multipart/form-data` upload this module needs (`FormData` bodies must NOT have an explicit
 * `Content-Type` — the browser sets it, boundary included). This module fetches its own CSRF token
 * from the same `/api/antiforgery/token` endpoint `client.ts`'s `initCsrf()` already uses (that
 * endpoint is idempotent and cheap — it just mints/returns a token — so a second, independent call
 * here duplicates one request, not any real logic).
 */

export interface DocumentSummary {
  id: number
  name: string
  mimeType: string
  sizeBytes: number
  uploadedById: number
  createdAt: string
  /** TASK-0160 (REQ-022-002): free-text note entered at upload time, or `null`/absent when none
   * was entered — `DocumentsController.List`'s projection only started including this after this
   * task. */
  notes?: string | null
  /** TASK-0205 (REQ-022-002): optional expiration date entered at upload time (ISO `date` string,
   * e.g. `"2027-01-31"`), or `null`/absent when none was entered — same "add alongside notes"
   * treatment as TASK-0160. */
  expirationDate?: string | null
  /** TASK-0249 (REQ-022-002): free-text document classification entered at upload time (e.g.
   * "Contratto", "Fattura"), or `null`/absent when none was entered — same "add alongside notes"
   * treatment as TASK-0160/TASK-0205. NOT `mimeType` — this is a business classification the
   * uploader typed, not the file's technical content type. */
  documentType?: string | null
  /** TASK-0249 (REQ-022-002): free-text status entered at upload time, or `null`/absent when none
   * was entered — same "add alongside notes" treatment as `documentType` above. Settable only at
   * upload time; there is no endpoint that changes it afterward (see
   * `Document.Status`'s backend remarks). */
  status?: string | null
  /** F-141 (REQ-029-008): `Completato` (il file e' sullo storage), `InCoda` (sara' caricato da un job) o `Errore`
   * (il caricamento non e' riuscito). Assente = `Completato`. */
  uploadStatus?: 'Completato' | 'InCoda' | 'Errore'
}

interface CsrfTokenBody {
  token: string
}

/** Tipi di documento che la lettura con IA sa leggere. */
export type DocumentReadKind = 'carta_identita' | 'bolletta' | 'visura'

/**
 * Tipi di documento di una pratica, scelti al caricamento o cambiati dopo: decidono la casella sui portali
 * (Ermes: bolletta -> Bolletta, carta_identita/visura -> Documento di identità, altro -> Documenti aggiuntivi).
 */
export const CONTRACT_DOCUMENT_TYPES: { value: string; label: string }[] = [
  { value: '', label: 'Senza tipo (dal nome del file)' },
  { value: 'bolletta', label: 'Bolletta / fattura' },
  { value: 'carta_identita', label: 'Documento di identità' },
  { value: 'visura', label: 'Visura camerale' },
  { value: 'pda_firmata', label: 'PDA / contratto firmato' },
  { value: 'altro', label: 'Altro (documenti aggiuntivi)' },
]

export function contractDocumentTypeLabel(type: string | null | undefined): string {
  const t = (type ?? '').trim()
  return CONTRACT_DOCUMENT_TYPES.find(x => x.value === t)?.label ?? (t || 'Senza tipo')
}

export interface DocumentReadField {
  name: string
  value: string
  confidence: number
}

export interface DocumentReadProposal {
  documentId: number
  kind: DocumentReadKind
  requiresConfirmation: boolean
  /** "modello" = letta con il modello salvato del fornitore (nessun costo), "ia" = letta dall'IA, "modello+ia" = modello completato dall'IA (dati tecnici). */
  source?: 'ia' | 'modello' | 'modello+ia'
  fields: DocumentReadField[]
}

export interface DocumentReadConfirmation {
  documentId: number
  kind: DocumentReadKind
  confirmed: boolean
  fields: Record<string, string>
}

async function csrfHeaders(): Promise<HeadersInit> {
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' })
  if (!res.ok) return {}
  const data = (await res.json()) as CsrfTokenBody
  return { 'X-XSRF-TOKEN': data.token }
}

async function jsonHeaders(): Promise<Headers> {
  const headers = new Headers(await csrfHeaders())
  headers.set('Content-Type', 'application/json')
  return headers
}

async function readErrorMessage(res: Response): Promise<string> {
  // TASK-0127: same bug class TASK-0126 closed in client.ts's `api()` — this parser never read
  // `body?.error`, the dominant error shape (46 sites across 17 controllers). Dormant here today
  // (DocumentsController.cs's 5 error sites all emit only `message`), but added for consistency
  // and to avoid a silent regression if that controller ever adopts the `error` shape. Ordering
  // matches client.ts's final fallback chain exactly (`title ?? message ?? error ?? fallback`)
  // for consistency across the two independent error parsers; harmless here since `title` is
  // never emitted by DocumentsController.cs, so this falls through to `message` exactly as before.
  const body = await res.json().catch(() => null) as { title?: string; message?: string; error?: string } | null
  return body?.title ?? body?.message ?? body?.error ?? `HTTP ${res.status}`
}

export const documentsApi = {
  /** `GET /api/documents?entityType=..&entityId=..` — same two query params the backend has
   * always supported (see `DocumentsController.List`). */
  list: async (entityType: string, entityId: number): Promise<DocumentSummary[]> => {
    const q = new URLSearchParams({ entityType, entityId: String(entityId) })
    const res = await fetch(`/api/documents?${q}`, { credentials: 'include' })
    if (!res.ok) throw new Error(await readErrorMessage(res))
    return res.json() as Promise<DocumentSummary[]>
  },

  /**
   * Real `multipart/form-data` upload (TASK-0032). `SizeBytes`/`MimeType` are computed
   * server-side from the actually-uploaded file — there is nowhere to pass a client-declared value
   * for either, on purpose (see `DocumentsController.Create`'s remarks on the backend).
   *
   * TASK-0160: optional fourth `notes` parameter, appended only when non-empty — same "don't send
   * a field for an unentered optional value" convention as this frontend's JSON request bodies.
   *
   * TASK-0205: optional fifth `expirationDate` parameter, appended only when present — same
   * omit-when-absent convention as `notes`.
   *
   * TASK-0249: optional sixth/seventh `documentType`/`status` parameters, appended only when
   * non-empty — same omit-when-absent convention as `notes`/`expirationDate`.
   */
  upload: async (
    entityType: string, entityId: number, file: File, notes?: string, expirationDate?: string,
    documentType?: string, status?: string,
  ): Promise<{ id: number; uploadStatus?: string }> => {
    const form = new FormData()
    form.append('EntityType', entityType)
    form.append('EntityId', String(entityId))
    form.append('File', file)
    if (notes) form.append('Notes', notes)
    if (expirationDate) form.append('ExpirationDate', expirationDate)
    if (documentType) form.append('DocumentType', documentType)
    if (status) form.append('Status', status)

    const res = await fetch('/api/documents', {
      method: 'POST',
      credentials: 'include',
      headers: await csrfHeaders(),
      body: form,
    })
    if (!res.ok) throw new Error(await readErrorMessage(res))
    return res.json() as Promise<{ id: number; uploadStatus?: string }>
  },

  /**
   * ADR-010 §4: streaming authenticated download through the API — NOT a presigned URL. This is a
   * plain same-origin path, meant to be used as an `<a href>`/`window.location` target: a normal
   * browser navigation to a same-origin URL already carries the session cookie, so no separate
   * fetch/blob dance is needed here for the download itself to be authenticated.
   */
  contentUrl: (id: number): string => `/api/documents/${id}/content`,

  /**
   * TASK-0254 (REQ-022-003): same same-origin content URL as `contentUrl`, plus
   * `?inline=true` — `DocumentsController.Content` honours it only for a closed allow-list of
   * browser-safe MIME types (falling back to the normal attachment download for everything else),
   * so this is always safe to open in a new tab regardless of the document's actual type.
   */
  previewUrl: (id: number): string => `/api/documents/${id}/content?inline=true`,

  /** `DELETE /api/documents/{id}` — Admin-only (unchanged from before this task); the caller must
   * be prepared for a 403 from a non-Admin session. */
  remove: async (id: number): Promise<void> => {
    const res = await fetch(`/api/documents/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: await csrfHeaders(),
    })
    if (!res.ok) throw new Error(await readErrorMessage(res))
  },

  /** `PATCH /api/documents/{id}/type` (staff): cambia il tipo del documento, che decide la casella sui portali. */
  setType: async (id: number, documentType: string | null): Promise<void> => {
    const headers = new Headers(await csrfHeaders())
    headers.set('Content-Type', 'application/json')
    const res = await fetch(`/api/documents/${id}/type`, {
      method: 'PATCH',
      credentials: 'include',
      headers,
      body: JSON.stringify({ documentType }),
    })
    if (!res.ok) throw new Error(await readErrorMessage(res))
  },

  /** `POST /api/documents/{id}/read-proposal` — l'IA legge il documento e propone i campi; niente viene salvato. */
  readProposal: async (id: number, kind: DocumentReadKind): Promise<DocumentReadProposal> => {
    const res = await fetch(`/api/documents/${id}/read-proposal`, {
      method: 'POST',
      credentials: 'include',
      headers: await jsonHeaders(),
      body: JSON.stringify({ kind }),
    })
    if (!res.ok) throw new Error(await readErrorMessage(res))
    return res.json() as Promise<DocumentReadProposal>
  },

  /** `POST /api/documents/read-file` — legge un file non ancora salvato (es. la bolletta in "Nuova pratica"). */
  readFile: async (file: File, kind: DocumentReadKind): Promise<Omit<DocumentReadProposal, 'documentId'>> => {
    const form = new FormData()
    form.append('Kind', kind)
    form.append('File', file)
    const res = await fetch('/api/documents/read-file', {
      method: 'POST',
      credentials: 'include',
      headers: await csrfHeaders(),
      body: form,
    })
    if (!res.ok) throw new Error(await readErrorMessage(res))
    return res.json() as Promise<Omit<DocumentReadProposal, 'documentId'>>
  },

  /** `POST /api/documents/{id}/read-confirm` — registra i valori controllati dall'operatore. */
  readConfirm: async (id: number, kind: DocumentReadKind, fields: Record<string, string>): Promise<DocumentReadConfirmation> => {
    const res = await fetch(`/api/documents/${id}/read-confirm`, {
      method: 'POST',
      credentials: 'include',
      headers: await jsonHeaders(),
      body: JSON.stringify({ kind, fields }),
    })
    if (!res.ok) throw new Error(await readErrorMessage(res))
    return res.json() as Promise<DocumentReadConfirmation>
  },
}
